import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";
import {
  EMAIL_DIGEST_FREQUENCIES,
  NOTIFICATION_CHANNELS,
  SNOOZE_DAYS_MAX,
  SNOOZE_DAYS_MIN,
} from "@amtana/notification-contract";
import type {
  EmailDigestFrequency,
  NOTIFICATION_ERROR_CODES,
  NotificationChannel,
  NotificationType,
} from "@amtana/notification-contract";

// Copy for the module's own codes, the settings screen's own words (S6.05) and
// the unsubscribe confirmation's (email-delivery.feature). `NOTIFICATION_TYPE_CATALOGUE`'s
// own `label`/`description` live in the contract package, which can never depend on
// `@amtana/i18n`, so the screen reads each type's words from this catalogue (`noticeTypeCopy`).

export type NotificationCopyKey = keyof typeof EN;
export type NotificationCatalog = typeof EN;

const EN = {
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  notificationsTitle: "Notifications",
  pageDescription: "Choose which events notify you, and on which channel.",
  notificationTypesTitle: "Notification types",
  pushPanelTitle: "Push",
  dndTitle: "Do not disturb",
  snoozeEmailTitle: "Snooze email",
  snoozeEmailDescription:
    "In-app notices still land; email waits until the snooze ends, then arrives as one summary.",
  snoozedUntil: "Snoozed until {date}",
  endSnooze: "End snooze",
  snoozeForLabel: "Snooze for",
  snoozeButton: "Snooze",
  snoozeDaysOption: { one: "1 day", other: "{count} days" },
  quietHoursLabel: "Quiet hours",
  quietHoursDescription: "A daily window, in your own time zone, email waits out.",
  fromLabel: "From",
  quietHoursToConnector: "to",
  toLabel: "To",
  emailDigestTitle: "Email digest",
  emailDigestDescription: "A roundup of everything, instead of one email per event.",
  digestImmediate: "Immediate",
  digestDaily: "Daily digest",
  digestWeekly: "Weekly digest",
  channelInApp: "In-app",
  channelEmail: "Email",
  channelPush: "Push",

  // Notification types: what the settings screen shows for each.
  typeScanFinishedLabel: "Scan finished",
  typeScanFinishedDescription: "An audience scan you started has finished.",
  typeScanFailedLabel: "Scan failed",
  typeScanFailedDescription: "An audience scan you started could not finish.",
  typeScanOutOfCreditsLabel: "Scan ran out of credits",
  typeScanOutOfCreditsDescription:
    "An audience scan you started ended because the organization ran out of credits.",
  typeCreditsLowLabel: "Credits low",
  typeCreditsLowDescription: "Your organization is running low on credits.",
  typeInviteReceivedLabel: "Invite received",
  typeInviteReceivedDescription: "Someone invited you to join an organization.",
  typePaymentFailedLabel: "Payment failed",
  typePaymentFailedDescription: "A payment for your organization's plan failed.",
  typeAccessRequestReceivedLabel: "Access request received",
  typeAccessRequestReceivedDescription: "Someone asked to join your organization.",
  typeAccessRequestApprovedLabel: "Access request approved",
  typeAccessRequestApprovedDescription: "Your request to join an organization was approved.",
  typeAccessRequestDeclinedLabel: "Access request declined",
  typeAccessRequestDeclinedDescription: "Your request to join an organization was declined.",
  typeUsageWarningLabel: "Usage warning",
  typeUsageWarningDescription:
    "Your organization is approaching or has reached its credit limit for the period.",
  typeRechargeFailedLabel: "Auto-recharge failed",
  typeRechargeFailedDescription: "An automatic credit top-up for your organization could not be charged.",
  typeManualSubscriptionEndedLabel: "Manual plan ended",
  typeManualSubscriptionEndedDescription: "A plan set by staff reached its end date and dropped to Free.",

  // How long ago a notice was raised.
  justNow: "Just now",
  minutesAgo: "{count}m ago",
  hoursAgo: "{count}h ago",
  daysAgo: "{count}d ago",

  // The bell.
  bellLabel: "Notifications",
  unreadCount: { one: "1 unread", other: "{count} unread" },
  allCaughtUp: "You're all caught up.",
  markAllRead: "Mark all read",
  noNotificationsTitle: "No notifications yet",
  noNotificationsDescription: "You'll see them here.",

  // The reasons a change did not go through.
  couldNotUpdateSetting: "Could not update that setting",
  couldNotUpdateChannel: "Could not update that channel",
  couldNotSnooze: "Could not snooze",
  couldNotEndSnooze: "Could not end the snooze",
  couldNotUpdateQuietHours: "Could not update quiet hours",
  couldNotUpdateDigest: "Could not update your digest",
  couldNotMarkRead: "Could not mark that notice read",
  couldNotMarkAllRead: "Could not mark every notice read",

  // Document title, navigation and the module's own errors.
  screenTitleSettings: "Notifications · {product}",
  screenTitleUnsubscribe: "Unsubscribe · {product}",
  noticeNotFoundTitle: "That notice is gone",
  noticeNotFoundDescription: "Refresh the list and try again.",
  snoozeOutOfRangeTitle: "Choose 1 to 7 days",
  snoozeOutOfRangeDescription: "A snooze must be between one and seven days.",
  slackChannelNotFoundTitle: "That Slack channel is gone",
  slackChannelNotFoundDescription: "Refresh the list and try again.",
  slackPostFailedTitle: "Slack did not accept the post",
  slackPostFailedDescription: "Check the webhook URL is still valid, then try again.",
  slackWebhookRefusedInProductionTitle: "That webhook URL is not allowed here",
  slackWebhookRefusedInProductionDescription: "Paste the real incoming-webhook URL Slack gave you.",
  unsubscribeTitle: "Stop these emails?",
  unsubscribeDescription:
    "You will no longer get this email. Notices still appear in the app, and you can turn email back on in your settings.",
  unsubscribeButton: "Unsubscribe",
  unsubscribingButton: "Unsubscribing…",
  unsubscribedTitle: "You are unsubscribed",
  unsubscribedDescription: "We will not send you this email again.",
  unsubscribeLinkInvalidTitle: "This link did not work",
  unsubscribeLinkInvalidDescription:
    "It may be incomplete or too old. You can turn email off in your notification settings instead.",
  unsubscribeFailed: "We could not unsubscribe you",
  notificationSettingsLink: "Notification settings",
} as const;

const ES: Translation<NotificationCatalog> = {
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Configuración",
  notificationsTitle: "Notificaciones",
  pageDescription: "Elige qué eventos te notifican y en qué canal.",
  notificationTypesTitle: "Tipos de notificación",
  pushPanelTitle: "Notificaciones push",
  dndTitle: "No molestar",
  snoozeEmailTitle: "Posponer correo",
  snoozeEmailDescription:
    "Los avisos en la aplicación siguen llegando; el correo espera hasta que termine la pausa y luego llega como un solo resumen.",
  snoozedUntil: "Pospuesto hasta {date}",
  endSnooze: "Terminar pausa",
  snoozeForLabel: "Posponer por",
  snoozeButton: "Posponer",
  snoozeDaysOption: { one: "1 día", other: "{count} días" },
  quietHoursLabel: "Horario de silencio",
  quietHoursDescription: "Una ventana diaria, en tu propia zona horaria, durante la cual el correo espera.",
  fromLabel: "Desde",
  quietHoursToConnector: "a",
  toLabel: "Hasta",
  emailDigestTitle: "Resumen por correo",
  emailDigestDescription: "Un resumen completo, en lugar de un correo por cada evento.",
  digestImmediate: "Inmediato",
  digestDaily: "Resumen diario",
  digestWeekly: "Resumen semanal",
  channelInApp: "En la aplicación",
  channelEmail: "Correo",
  channelPush: "Push",

  typeScanFinishedLabel: "Escaneo terminado",
  typeScanFinishedDescription: "Un escaneo de audiencia que iniciaste ha terminado.",
  typeScanFailedLabel: "Escaneo fallido",
  typeScanFailedDescription: "Un escaneo de audiencia que iniciaste no pudo terminar.",
  typeScanOutOfCreditsLabel: "Escaneo sin créditos",
  typeScanOutOfCreditsDescription:
    "Un escaneo de audiencia que iniciaste terminó porque la organización se quedó sin créditos.",
  typeCreditsLowLabel: "Créditos bajos",
  typeCreditsLowDescription: "A tu organización le quedan pocos créditos.",
  typeInviteReceivedLabel: "Invitación recibida",
  typeInviteReceivedDescription: "Alguien te invitó a unirte a una organización.",
  typePaymentFailedLabel: "Pago fallido",
  typePaymentFailedDescription: "Falló un pago del plan de tu organización.",
  typeAccessRequestReceivedLabel: "Solicitud de acceso recibida",
  typeAccessRequestReceivedDescription: "Alguien pidió unirse a tu organización.",
  typeAccessRequestApprovedLabel: "Solicitud de acceso aprobada",
  typeAccessRequestApprovedDescription: "Tu solicitud para unirte a una organización fue aprobada.",
  typeAccessRequestDeclinedLabel: "Solicitud de acceso rechazada",
  typeAccessRequestDeclinedDescription: "Tu solicitud para unirte a una organización fue rechazada.",
  typeUsageWarningLabel: "Aviso de uso",
  typeUsageWarningDescription:
    "Tu organización se acerca al límite de créditos del período, o ya lo alcanzó.",
  typeRechargeFailedLabel: "Falló la recarga automática",
  typeRechargeFailedDescription: "No se pudo cobrar una recarga automática de créditos de tu organización.",
  typeManualSubscriptionEndedLabel: "Plan manual terminado",
  typeManualSubscriptionEndedDescription:
    "Un plan establecido por el staff llegó a su fecha de término y bajó a Free.",

  justNow: "Ahora mismo",
  minutesAgo: "hace {count} min",
  hoursAgo: "hace {count} h",
  daysAgo: "hace {count} d",

  bellLabel: "Notificaciones",
  unreadCount: { one: "1 sin leer", other: "{count} sin leer" },
  allCaughtUp: "Estás al día.",
  markAllRead: "Marcar todos como leídos",
  noNotificationsTitle: "Aún no hay notificaciones",
  noNotificationsDescription: "Las verás aquí.",

  couldNotUpdateSetting: "No se pudo actualizar ese ajuste",
  couldNotUpdateChannel: "No se pudo actualizar ese canal",
  couldNotSnooze: "No se pudo posponer",
  couldNotEndSnooze: "No se pudo terminar la pausa",
  couldNotUpdateQuietHours: "No se pudo actualizar el horario de silencio",
  couldNotUpdateDigest: "No se pudo actualizar tu resumen",
  couldNotMarkRead: "No se pudo marcar ese aviso como leído",
  couldNotMarkAllRead: "No se pudo marcar todos los avisos como leídos",

  screenTitleSettings: "Notificaciones · {product}",
  screenTitleUnsubscribe: "Darme de baja · {product}",
  noticeNotFoundTitle: "Ese aviso ya no está",
  noticeNotFoundDescription: "Actualiza la lista e inténtalo de nuevo.",
  snoozeOutOfRangeTitle: "Elige de 1 a 7 días",
  snoozeOutOfRangeDescription: "Una pausa debe durar entre uno y siete días.",
  slackChannelNotFoundTitle: "Ese canal de Slack ya no está",
  slackChannelNotFoundDescription: "Actualiza la lista e inténtalo de nuevo.",
  slackPostFailedTitle: "Slack no aceptó la publicación",
  slackPostFailedDescription: "Revisa que la URL de webhook siga siendo válida e inténtalo de nuevo.",
  slackWebhookRefusedInProductionTitle: "Esa URL de webhook no está permitida aquí",
  slackWebhookRefusedInProductionDescription: "Pega la URL real del webhook entrante que te dio Slack.",
  unsubscribeTitle: "¿Dejar de recibir estos correos?",
  unsubscribeDescription:
    "Ya no recibirás este correo. Los avisos siguen apareciendo en la aplicación, y puedes volver a activar el correo en tu configuración.",
  unsubscribeButton: "Darme de baja",
  unsubscribingButton: "Dándote de baja…",
  unsubscribedTitle: "Te diste de baja",
  unsubscribedDescription: "No volveremos a enviarte este correo.",
  unsubscribeLinkInvalidTitle: "Este enlace no funcionó",
  unsubscribeLinkInvalidDescription:
    "Puede estar incompleto o ser demasiado antiguo. Puedes desactivar el correo en tu configuración de notificaciones.",
  unsubscribeFailed: "No pudimos darte de baja",
  notificationSettingsLink: "Configuración de notificaciones",
};

export const NOTIFICATION_CATALOGS: Catalogs<NotificationCatalog> = { en: EN, es: ES };

/** The snooze-until banner's own date; the caller formats `date` in the active language. */
export function snoozedUntilText(t: Translate<NotificationCatalog>, date: string): string {
  return t("snoozedUntil", { date });
}

/** One option per allowed snooze length, "1 day" through "{SNOOZE_DAYS_MAX} days". */
export function snoozeOptions(t: Translate<NotificationCatalog>): ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> {
  return Array.from(
    { length: SNOOZE_DAYS_MAX - SNOOZE_DAYS_MIN + 1 },
    (_, index) => SNOOZE_DAYS_MIN + index,
  ).map((days) => ({
    value: String(days),
    label: t("snoozeDaysOption", { count: days }),
  }));
}

function channelLabel(t: Translate<NotificationCatalog>, channel: NotificationChannel): string {
  switch (channel) {
    case "inApp":
      return t("channelInApp");
    case "email":
      return t("channelEmail");
    case "push":
      return t("channelPush");
  }
}

/** Every channel a setting can choose, in catalogue order, with its translated label. */
export function channelOptions(
  t: Translate<NotificationCatalog>,
): ReadonlyArray<{ readonly channel: NotificationChannel; readonly label: string }> {
  return NOTIFICATION_CHANNELS.map((channel) => ({ channel, label: channelLabel(t, channel) }));
}

function digestLabel(t: Translate<NotificationCatalog>, frequency: EmailDigestFrequency): string {
  switch (frequency) {
    case "immediate":
      return t("digestImmediate");
    case "daily":
      return t("digestDaily");
    case "weekly":
      return t("digestWeekly");
  }
}

/** Every email digest frequency, in catalogue order, with its translated label. */
export function digestOptions(
  t: Translate<NotificationCatalog>,
): ReadonlyArray<{ readonly value: EmailDigestFrequency; readonly label: string }> {
  return EMAIL_DIGEST_FREQUENCIES.map((value) => ({ value, label: digestLabel(t, value) }));
}

export const QUIET_HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${String(hour).padStart(2, "0")}:00`,
}));

export const NOTIFICATION_ERROR_COPY = {
  notification_notice_not_found: {
    title: phrase(NOTIFICATION_CATALOGS, "noticeNotFoundTitle"),
    description: phrase(NOTIFICATION_CATALOGS, "noticeNotFoundDescription"),
  },
  notification_snooze_out_of_range: {
    title: phrase(NOTIFICATION_CATALOGS, "snoozeOutOfRangeTitle"),
    description: phrase(NOTIFICATION_CATALOGS, "snoozeOutOfRangeDescription"),
  },
  notification_slack_channel_not_found: {
    title: phrase(NOTIFICATION_CATALOGS, "slackChannelNotFoundTitle"),
    description: phrase(NOTIFICATION_CATALOGS, "slackChannelNotFoundDescription"),
  },
  notification_slack_post_failed: {
    title: phrase(NOTIFICATION_CATALOGS, "slackPostFailedTitle"),
    description: phrase(NOTIFICATION_CATALOGS, "slackPostFailedDescription"),
  },
  notification_slack_webhook_refused_in_production: {
    title: phrase(NOTIFICATION_CATALOGS, "slackWebhookRefusedInProductionTitle"),
    description: phrase(NOTIFICATION_CATALOGS, "slackWebhookRefusedInProductionDescription"),
  },
} satisfies ErrorCopyFor<typeof NOTIFICATION_ERROR_CODES>;

const TYPE_COPY: Readonly<
  Record<
    NotificationType,
    { readonly label: PlainKey<NotificationCatalog>; readonly description: PlainKey<NotificationCatalog> }
  >
> = {
  "scan-finished": { label: "typeScanFinishedLabel", description: "typeScanFinishedDescription" },
  "scan-failed": { label: "typeScanFailedLabel", description: "typeScanFailedDescription" },
  "scan-out-of-credits": {
    label: "typeScanOutOfCreditsLabel",
    description: "typeScanOutOfCreditsDescription",
  },
  "credits-low": { label: "typeCreditsLowLabel", description: "typeCreditsLowDescription" },
  "invite-received": { label: "typeInviteReceivedLabel", description: "typeInviteReceivedDescription" },
  "payment-failed": { label: "typePaymentFailedLabel", description: "typePaymentFailedDescription" },
  "access-request-received": {
    label: "typeAccessRequestReceivedLabel",
    description: "typeAccessRequestReceivedDescription",
  },
  "access-request-approved": {
    label: "typeAccessRequestApprovedLabel",
    description: "typeAccessRequestApprovedDescription",
  },
  "access-request-declined": {
    label: "typeAccessRequestDeclinedLabel",
    description: "typeAccessRequestDeclinedDescription",
  },
  "billing-usage-warning": { label: "typeUsageWarningLabel", description: "typeUsageWarningDescription" },
  "billing-recharge-failed": {
    label: "typeRechargeFailedLabel",
    description: "typeRechargeFailedDescription",
  },
  "manual-subscription-ended": {
    label: "typeManualSubscriptionEndedLabel",
    description: "typeManualSubscriptionEndedDescription",
  },
};

/** What the settings screen shows for one notification type, in the active language. */
export function noticeTypeCopy(
  t: Translate<NotificationCatalog>,
  type: NotificationType,
): { readonly label: string; readonly description: string } {
  const keys = TYPE_COPY[type];
  return { label: t(keys.label), description: t(keys.description) };
}

export function noticeTypeLabel(t: Translate<NotificationCatalog>, type: NotificationType): string {
  return noticeTypeCopy(t, type).label;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** A short "how long ago" for a notice's timestamp; `now` comes from the caller's clock. */
export function noticeAge(createdAt: string, now: number, t: Translate<NotificationCatalog>): string {
  const elapsed = Math.max(0, now - Date.parse(createdAt));
  if (elapsed < MINUTE_MS) return t("justNow");
  if (elapsed < HOUR_MS) return t("minutesAgo", { count: Math.floor(elapsed / MINUTE_MS) });
  if (elapsed < DAY_MS) return t("hoursAgo", { count: Math.floor(elapsed / HOUR_MS) });
  return t("daysAgo", { count: Math.floor(elapsed / DAY_MS) });
}

/** Where the unsubscribe confirmation screen is: nothing changes until the person presses the button. */
export type UnsubscribeScreenState = "confirm" | "pending" | "done" | "invalid" | "failed";

/** The screen's state from the link's token and what the button's POST answered so far. */
export function unsubscribeScreenState(input: {
  readonly token: string | null;
  readonly pending: boolean;
  readonly outcome: { readonly ok: boolean } | undefined;
  readonly failed: boolean;
}): UnsubscribeScreenState {
  if (!input.token) return "invalid";
  if (input.pending) return "pending";
  if (input.failed) return "failed";
  if (!input.outcome) return "confirm";
  return input.outcome.ok ? "done" : "invalid";
}
